import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { generateId } from '../shared/lib/id';
import { createRoom } from '../entities/room/lib/factory';
import { cloneRoom, createRoomItemsFromServices } from '../entities/room/lib/operations';
import { syncProjectPrices, hasAnySyncChange } from '../entities/estimate/lib/synchronizer';
import type { SyncStats } from '../entities/estimate/lib/synchronizer';
import { createProjectSnapshot as createSnapshot, updateProjectEntry, getProjectLibrary } from '../entities/estimate/lib/project';
import { computeEstimateTotals } from '../entities/estimate/lib/computeTotals';
import { track } from '@/shared/lib/analytics';
import { Room, EstimateItem, Service, ProjectSnapshot, ProjectMeta, Opening, Partition, WallElement } from '@/shared/model';
import { RoomSchema, ActStateSchema } from '@/shared/validation/schemas';
import type { ActState, CompletionAct } from '@/shared/validation/schemas';
import { STARTER_TEMPLATES } from '../entities/room/data/starterTemplates';
import { buildRoomFromTemplate, resolveServicesFromRecipeIds } from '../features/room/lib/createRoomFromTemplate';
import i18n from '@/shared/config/i18n/i18n';
import { useRecipeStore } from './useRecipeStore';
import { useServiceStore } from './useServiceStore';
import { migrateProjectStore } from './projectMigrations';
import { useAuthStore } from '@/stores/useAuthStore';
import { SYNC_PRODUCER_ENABLED } from '@/shared/config/syncFlags';
import { enqueueProjectChange } from './syncQueueBridge';
import {
  buildRoomUpsertRecord,
  buildRoomDeleteRecord,
  resolveEnqueueProjectId,
  buildProjectItemsRecord,
  buildProjectMetaRecord,
} from '@/shared/lib/syncProtocol';

/**
 * localStorage key for the FUNNEL-02 once-per-user activation guard.
 * Set BEFORE calling track() to prevent double-fire on race (Pitfall 2).
 * Server SELECT-before-INSERT dedup (18-01) is the multi-device backstop.
 * Outside Zustand state to avoid triggering the autosave subscriber.
 */
const ACTIVATION_LS_KEY = 'estimatix-activation-tracked';

/** Safe default ActState — used when snapshot.actState is absent (backward-compat). */
const EMPTY_ACT_STATE: ActState = { acts: [], closedByItem: {} };

export type { ProjectMeta };

// Helper to handle both value and functional updates
function applyUpdater<T>(updater: T | ((prev: T) => T), prev: T): T {
  return typeof updater === 'function' ? (updater as (prev: T) => T)(prev) : updater;
}

// ---------------------------------------------------------------------------
// Producer write-back infrastructure (Phase 36 / SYNC-02)
// Module-level to avoid per-keystroke enqueue floods. Mirrors
// src/stores/useServiceStore.ts's pendingSyncTimers/scheduleSync verbatim,
// keyed by roomId instead of serviceId.
// Pitfall 7 (mirrored): triggers live INSIDE action bodies — NOT in a set()
// subscriber. useProjectStore's pre-existing startAutoSave subscriber is left
// completely untouched by this plan; the producer trigger is never added there
// (T-04-12 echo-loop discipline — see resolveProjectIdForEnqueue below).
// ---------------------------------------------------------------------------

/** Per-roomId debounce timers — keyed by id to avoid cross-room interference. */
const pendingRoomSyncTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Schedule a debounced room sync. Clears any existing timer for the same
 * roomId before setting a new one, so two edits to the same room inside one
 * window collapse into exactly one queued record reflecting the LAST call's
 * state at fire-time (read via get() inside syncFn, never a pre-set() closure
 * value).
 */
function scheduleRoomSync(roomId: string, syncFn: () => void, delayMs = 500): void {
  const existing = pendingRoomSyncTimers.get(roomId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    pendingRoomSyncTimers.delete(roomId);
    syncFn();
  }, delayMs);
  pendingRoomSyncTimers.set(roomId, timer);
}

/**
 * Shared guest-gate + kill-switch decision. Every instrumented action calls
 * this — never re-derives userId/SYNC_PRODUCER_ENABLED itself — so the check
 * cannot drift between the many call sites (36-RESEARCH.md §Don't Hand-Roll).
 * Returns the projectId to enqueue against, or null when enqueueing must not
 * happen (guest, disabled producer, or no active project).
 */
function resolveProjectIdForEnqueue(activeProjectId: string | number | null): string | null {
  const { userId } = useAuthStore.getState();
  return resolveEnqueueProjectId(userId, SYNC_PRODUCER_ENABLED, activeProjectId);
}

/**
 * CR-01 fix (36-REVIEW.md): project-level debounce timers capture `projectId`
 * synchronously at SCHEDULE time, but read `projectItems`/`projectOverrides`/
 * `safetyMargin`/`meta` via a fresh `get()` at FIRE time (500ms later). If the
 * active project changed in between (project switch, snapshot load, full
 * reset), the fire-time read returns the NEW active project's data while the
 * enqueue still ships it under the STALE captured `projectId` — silently
 * overwriting the stale project's server row with the wrong project's data.
 * This guard re-checks the active project at fire time and no-ops the stale
 * timer instead. Mirrors the identity check `triggerRoomSync` gets for free
 * from globally-unique room ids, but the project layer has no such per-record
 * identity, so an explicit check is required.
 */
function isStaleProjectSync(get: () => ProjectState, projectId: string): boolean {
  return resolveProjectIdForEnqueue(get().activeProjectId) !== projectId;
}

/**
 * Debounced producer trigger shared by every 500ms-debounced room-mutating
 * action (updateRoom, updateRoomName, updateRoomDims, updateRoomOpenings,
 * updateRoomPartitions, updateRoomWallElements, updateItem, removeItem,
 * addServicesToRoom). Resolves the guest gate / kill switch ONCE at call
 * time (mirroring useServiceStore.updateService's `if (!userId) return`
 * check before scheduleSync), then schedules a debounced enqueue that reads
 * the LATEST room via get() at fire-time — never a pre-set() closure value.
 */
function triggerRoomSync(get: () => ProjectState, roomId: string): void {
  const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
  if (!projectId) return;
  scheduleRoomSync(roomId, () => {
    const room = get().rooms.find((r) => r.id === roomId);
    if (!room) return;
    void enqueueProjectChange(buildRoomUpsertRecord(projectId, room));
  });
}

// ---------------------------------------------------------------------------
// Project (global-layer) producer write-back infrastructure (Plan 36-04, SYNC-02)
// Second module-level debounce map, keyed by projectId rather than roomId — one
// timer per active project is enough, the project layer has no per-field
// concurrency to separate. Item-layer and meta-layer records share this SAME
// map but use DISTINCT key suffixes (`:items` / `:meta`) derived from the same
// projectId, so a burst mixing both kinds of edits does not let one shadow the
// other under a single collapsed timer (T-04-12).
// ---------------------------------------------------------------------------

/** Per-key ("{projectId}:items" | "{projectId}:meta") debounce timers. */
const pendingProjectSyncTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Schedule a debounced project-layer sync under `key`. Mirrors scheduleRoomSync
 * verbatim, just keyed by a generic string instead of a roomId.
 */
function scheduleProjectSync(key: string, syncFn: () => void, delayMs = 500): void {
  const existing = pendingProjectSyncTimers.get(key);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    pendingProjectSyncTimers.delete(key);
    syncFn();
  }, delayMs);
  pendingProjectSyncTimers.set(key, timer);
}

interface ProjectState {
  rooms: Room[];
  projectItems: EstimateItem[];
  projectOverrides: {
    floorArea?: number;
    wallArea?: number;
    perimeter?: number;
  };
  meta: ProjectMeta;
  activeProjectId: string | number | null;
  safetyMargin: number;
  activeRoomId: string | null;
  /**
   * DOC-03: completion act state for this project.
   * Defaults to {acts:[], closedByItem:{}} (backward-compat — absent in pre-Phase-7 snapshots).
   * Persists as part of the project snapshot (versioned history) via createProjectSnapshot.
   */
  actState: ActState;

  setRooms: (roomsOrUpdater: Room[] | ((prev: Room[]) => Room[])) => void;
  /**
   * WR-01 fix (36-REVIEW.md): applies the server's authoritative post-merge
   * global layer (projectItems/projectOverrides/safetyMargin/meta) back into
   * local state after a successful flush. Deliberately UN-instrumented (no
   * enqueueProjectChange call) — mirrors setRooms's echo-loop-safe shape,
   * since this writes SERVER-origin data, not a user edit. Without this, the
   * in-memory editor silently diverges from what is actually persisted
   * server-side after every flush, and the next debounced local edit would
   * re-push stale pre-flush values, capable of clobbering a concurrent
   * device's newer global-layer write.
   */
  applyServerMergedGlobalLayer: (data: {
    projectItems: EstimateItem[];
    projectOverrides: ProjectState['projectOverrides'];
    safetyMargin: number;
    meta: ProjectMeta;
  }) => void;
  setProjectItems: (itemsOrUpdater: EstimateItem[] | ((prev: EstimateItem[]) => EstimateItem[])) => void;
  setProjectOverrides: (overrides: Partial<ProjectState['projectOverrides']>) => void;
  setMeta: (metaOrUpdater: ProjectMeta | ((prev: ProjectMeta) => ProjectMeta)) => void;
  setActiveProjectId: (id: string | number | null) => void;
  setSafetyMargin: (margin: number) => void;
  setActiveRoomId: (id: string | null) => void;
  updateMeta: (updates: Partial<ProjectMeta>) => void;
  addRoom: (activeRoomId?: string | null) => void;
  deleteRoom: (id: string) => void;
  duplicateRoom: (id: string) => void;
  updateRoom: (roomId: string, updates: Partial<Room>) => void;
  updateRoomName: (id: string, newName: string) => void;
  updateRoomDims: (activeRoomId: string | null, newDims: Partial<Room['dims']>) => void;
  updateRoomOpenings: (activeRoomId: string | null, newOpenings: Opening[]) => void;
  updateRoomPartitions: (activeRoomId: string | null, newPartitions: Partition[]) => void;
  updateRoomWallElements: (activeRoomId: string | null, newElements: WallElement[]) => void;
  updateItem: (activeRoomId: string | null, itemId: string, field: keyof EstimateItem, value: EstimateItem[keyof EstimateItem]) => void;
  removeItem: (activeRoomId: string | null, itemId: string) => void;
  addServicesToRoom: (roomId: string, servicesToAdd: Service[]) => void;
  resetRooms: () => void;
  syncPrices: (servicesDb: Service[] | Record<string, Service[]>) => SyncStats;
  // project global layer (items + overrides for проектная геометрия / manual)
  addProjectItem: (item: EstimateItem) => void;
  updateProjectItem: (itemId: string, field: keyof EstimateItem, value: EstimateItem[keyof EstimateItem]) => void;
  removeProjectItem: (itemId: string) => void;
  addServicesToProject: (servicesToAdd: Service[]) => void;
  createProjectSnapshot: (activeRoomId: string | null, name?: string) => ProjectSnapshot;
  loadProjectSnapshot: (snapshot: ProjectSnapshot) => { activeRoomId: string | null };
  commitVersion: (name: string) => Promise<void>;
  initRooms: () => void;
  startAutoSave: () => () => void;
  /**
   * DOC-03: Append a completed act + update closedByItem in the project's ActState.
   * Writes through the existing snapshot update path so acts join versioned project history.
   * CLAUDE.md rule 11: never lose act data; this is capital.
   *
   * @param act             - The CompletionAct built by buildActData (already validated)
   * @param updatedActState - The ActState returned by buildActData (has new act + closedByItem)
   */
  appendAct: (act: CompletionAct, updatedActState: ActState) => void;
  /**
   * Create a room from a starter template (TMPL-01).
   * Builds the room via buildRoomFromTemplate, appends it to rooms,
   * resolves services from template recipeIds using current recipe + service store state,
   * and calls addServicesToRoom to produce a populated estimate.
   * Returns the new room id so the UI can navigate to it.
   * Does NOT bump the persist version.
   */
  addRoomFromTemplate: (templateId: string) => string;
}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      rooms: [],
      projectItems: [],
      projectOverrides: {},
      meta: {} as ProjectMeta,
      activeProjectId: null as string | number | null,
      safetyMargin: 0,
      activeRoomId: null as string | null,
      actState: EMPTY_ACT_STATE,

      setRooms: (roomsOrUpdater) => set((state) => ({ rooms: applyUpdater(roomsOrUpdater, state.rooms) })),
      // WR-01 fix: deliberately UN-instrumented, same rationale as setRooms above —
      // this applies SERVER-origin post-merge data, never a user edit, so it must
      // never trigger enqueueProjectChange (that would echo the server's own data
      // back at it under a debounced timer, and could race a newer edit).
      applyServerMergedGlobalLayer: (data) =>
        set({
          projectItems: data.projectItems,
          projectOverrides: data.projectOverrides,
          safetyMargin: data.safetyMargin,
          meta: data.meta,
        }),
      // setProjectItems IS instrumented (deliberately, per 36-04-PLAN.md): it is an
      // exported mutating action on the global layer; leaving it silent would be
      // exactly the latent silent-loss path this phase exists to close.
      setProjectItems: (itemsOrUpdater) => {
        set((state) => ({ projectItems: applyUpdater(itemsOrUpdater, state.projectItems) }));
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          scheduleProjectSync(`${projectId}:items`, () => {
            if (isStaleProjectSync(get, projectId)) return; // CR-01 guard
            const { projectItems, projectOverrides, safetyMargin } = get();
            void enqueueProjectChange(buildProjectItemsRecord(projectId, { projectItems, projectOverrides, safetyMargin }));
          });
        }
      },
      setProjectOverrides: (overrides) => {
        set((state) => ({ projectOverrides: { ...state.projectOverrides, ...overrides } }));
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          scheduleProjectSync(`${projectId}:items`, () => {
            if (isStaleProjectSync(get, projectId)) return; // CR-01 guard
            const { projectItems, projectOverrides, safetyMargin } = get();
            void enqueueProjectChange(buildProjectItemsRecord(projectId, { projectItems, projectOverrides, safetyMargin }));
          });
        }
      },
      // setMeta IS instrumented (deliberately, per 36-04-PLAN.md) for the same reason
      // as setProjectItems above — an exported global-layer mutator must not be a
      // silent producer gap.
      setMeta: (metaOrUpdater) => {
        set((state) => ({ meta: applyUpdater(metaOrUpdater, state.meta) }));
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          scheduleProjectSync(`${projectId}:meta`, () => {
            void (async () => {
              if (isStaleProjectSync(get, projectId)) return; // CR-01 guard
              const { meta } = get();
              const library = await getProjectLibrary();
              const entry = library.find((p) => String(p.id) === projectId);
              void enqueueProjectChange(buildProjectMetaRecord(projectId, entry?.name, meta));
            })();
          });
        }
      },
      setActiveProjectId: (id) => set({ activeProjectId: id }),
      setSafetyMargin: (margin) => {
        set({ safetyMargin: margin });
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          scheduleProjectSync(`${projectId}:items`, () => {
            if (isStaleProjectSync(get, projectId)) return; // CR-01 guard
            const { projectItems, projectOverrides, safetyMargin } = get();
            void enqueueProjectChange(buildProjectItemsRecord(projectId, { projectItems, projectOverrides, safetyMargin }));
          });
        }
      },
      setActiveRoomId: (id) => set({ activeRoomId: id }),

      updateMeta: (updates) => {
        set((state) => ({ meta: { ...state.meta, ...updates } }));
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          scheduleProjectSync(`${projectId}:meta`, () => {
            void (async () => {
              if (isStaleProjectSync(get, projectId)) return; // CR-01 guard
              const { meta } = get();
              const library = await getProjectLibrary();
              const entry = library.find((p) => String(p.id) === projectId);
              void enqueueProjectChange(buildProjectMetaRecord(projectId, entry?.name, meta));
            })();
          });
        }
      },

      addRoom: (activeRoomId) => {
        // Restructured into a statement body (from a bare set() expression) to capture
        // the new room and trigger the immediate producer enqueue after it — mirrors
        // useServiceStore.createService. The state-update behaviour itself is unchanged.
        const currentRoom = get().rooms.find((r) => r.id === activeRoomId) || get().rooms[0];
        const defaultHeight = currentRoom ? currentRoom.dims.height : 0;
        const newId = generateId();
        const newRoom = createRoom(newId, '', { dims: { length: 0, width: 0, height: defaultHeight } });
        set((state) => ({ rooms: [...state.rooms, newRoom] }));

        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          void enqueueProjectChange(buildRoomUpsertRecord(projectId, newRoom));
        }
      },

      addRoomFromTemplate: (templateId) => {
        const template = STARTER_TEMPLATES.find((t) => t.id === templateId);
        if (!template) {
          console.warn(`[useProjectStore] addRoomFromTemplate: unknown templateId "${templateId}"`);
          // Fall back to a blank room so the UI is never left in a broken state
          const fallbackId = generateId();
          const fallbackRoom = createRoom(fallbackId, '');
          set((state) => ({ rooms: [...state.rooms, fallbackRoom] }));
          const fallbackProjectId = resolveProjectIdForEnqueue(get().activeProjectId);
          if (fallbackProjectId) {
            void enqueueProjectChange(buildRoomUpsertRecord(fallbackProjectId, fallbackRoom));
          }
          return fallbackId;
        }
        const translatedName = i18n.t(template.nameKey, template.name);
        const newRoom = buildRoomFromTemplate(template, translatedName);
        // Append room first (two separate set() calls to keep each atomic and avoid stale captures)
        set((state) => ({ rooms: [...state.rooms, newRoom] }));
        // Resolve services from current store state (offline-safe — reads persisted data)
        const recipes = useRecipeStore.getState().recipes;
        const serviceMap: Record<string, Service> = Object.fromEntries(
          useServiceStore.getState().rawServices.map((s) => [s.id, s])
        );
        const resolvedServices = resolveServicesFromRecipeIds(template.recipeIds, recipes, serviceMap);
        if (resolvedServices.length > 0) {
          set((state) => ({
            rooms: state.rooms.map((r) => {
              if (r.id !== newRoom.id) return r;
              const newItems = createRoomItemsFromServices(resolvedServices);
              return { ...r, items: [...r.items, ...newItems] };
            }),
          }));
        }

        // Immediate enqueue (no debounce): reads the FINAL post-mutation room via get()
        // so a template's resolved services are included in the same record, not a
        // stale pre-set() closure value.
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          const finalRoom = get().rooms.find((r) => r.id === newRoom.id);
          if (finalRoom) void enqueueProjectChange(buildRoomUpsertRecord(projectId, finalRoom));
        }
        return newRoom.id;
      },

      deleteRoom: (id) => {
        // IN-04 discipline (T-36-05): cancel any pending debounce timer for this room
        // BEFORE removal, so it cannot fire up to 500ms after the room no longer exists.
        const pendingTimer = pendingRoomSyncTimers.get(id);
        if (pendingTimer) {
          clearTimeout(pendingTimer);
          pendingRoomSyncTimers.delete(id);
        }
        set((state) => ({ rooms: state.rooms.filter((r) => r.id !== id) }));
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          void enqueueProjectChange(buildRoomDeleteRecord(projectId, id));
        }
      },

      duplicateRoom: (id) => {
        // Restructured into a statement body (from a bare set() expression) to capture
        // the cloned room and trigger the immediate producer enqueue after it.
        const roomToClone = get().rooms.find((r) => r.id === id);
        if (!roomToClone) return;
        const cloned = cloneRoom(roomToClone);
        if (!cloned) return;
        set((state) => ({ rooms: [...state.rooms, cloned] }));

        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          void enqueueProjectChange(buildRoomUpsertRecord(projectId, cloned));
        }
      },

      updateRoom: (roomId, updates) => {
        set((state) => ({
          rooms: state.rooms.map((r) => r.id === roomId ? { ...r, ...updates } : r),
        }));
        triggerRoomSync(get, roomId);
      },

      updateRoomName: (id, newName) => {
        set((state) => ({
          rooms: state.rooms.map((r) => r.id === id ? { ...r, name: newName } : r),
        }));
        triggerRoomSync(get, id);
      },

      updateRoomDims: (activeRoomId, newDims) => {
        set((state) => ({
          rooms: state.rooms.map((r) => r.id === activeRoomId ? { ...r, dims: { ...r.dims, ...newDims } } : r),
        }));
        if (activeRoomId) triggerRoomSync(get, activeRoomId);
      },

      updateRoomOpenings: (activeRoomId, newOpenings) => {
        set((state) => ({
          rooms: state.rooms.map((r) => r.id === activeRoomId ? { ...r, openings: newOpenings } : r),
        }));
        if (activeRoomId) triggerRoomSync(get, activeRoomId);
      },

      updateRoomPartitions: (activeRoomId, newPartitions) => {
        set((state) => ({
          rooms: state.rooms.map((r) => r.id === activeRoomId ? { ...r, partitions: newPartitions } : r),
        }));
        if (activeRoomId) triggerRoomSync(get, activeRoomId);
      },

      updateRoomWallElements: (activeRoomId, newElements) => {
        let accepted = false;
        set((state) => {
          const room = state.rooms.find((r) => r.id === activeRoomId);
          if (!room) return state;
          const candidate: Room = { ...room, wallElements: newElements };
          const result = RoomSchema.safeParse(candidate);
          if (!result.success) {
            return state; // no-op — invalid wallElements rejected (CLAUDE.md rule 11/12)
          }
          accepted = true;
          return { rooms: state.rooms.map((r) => r.id === activeRoomId ? candidate : r) };
        });
        // Trigger ONLY on the success path — a rejected edit is a no-op in state and
        // must be a no-op in the queue (36-03-PLAN.md behavior contract).
        if (accepted && activeRoomId) triggerRoomSync(get, activeRoomId);
      },

      updateItem: (activeRoomId, itemId, field, value) => {
        set((state) => ({
          rooms: state.rooms.map((r) => {
            if (r.id !== activeRoomId) return r;
            return { ...r, items: r.items.map((item) => item.id === itemId ? { ...item, [field]: value } : item) };
          }),
        }));
        if (activeRoomId) triggerRoomSync(get, activeRoomId);
      },

      removeItem: (activeRoomId, itemId) => {
        set((state) => ({
          rooms: state.rooms.map((r) => {
            if (r.id !== activeRoomId) return r;
            return { ...r, items: r.items.filter((i) => i.id !== itemId) };
          }),
        }));
        if (activeRoomId) triggerRoomSync(get, activeRoomId);
      },

      addServicesToRoom: (roomId, servicesToAdd) => {
        set((state) => ({
          rooms: state.rooms.map((r) => {
            if (r.id !== roomId) return r;
            const newItems = createRoomItemsFromServices(servicesToAdd);
            return { ...r, items: [...r.items, ...newItems] };
          }),
        }));
        triggerRoomSync(get, roomId);
      },

      // Project global layer (items live at project level for global логика; overrides for проектная геометрия)
      // Each action below triggers a debounced buildProjectItemsRecord enqueue on
      // the SAME "${projectId}:items" key, so a burst of edits collapses to one
      // queued record reflecting the LAST call's state (read fresh via get() at
      // fire-time inside scheduleProjectSync, never a pre-set() closure value).
      addProjectItem: (item) => {
        set((state) => ({ projectItems: [...state.projectItems, item] }));
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          scheduleProjectSync(`${projectId}:items`, () => {
            if (isStaleProjectSync(get, projectId)) return; // CR-01 guard
            const { projectItems, projectOverrides, safetyMargin } = get();
            void enqueueProjectChange(buildProjectItemsRecord(projectId, { projectItems, projectOverrides, safetyMargin }));
          });
        }
      },
      updateProjectItem: (itemId, field, value) => {
        set((state) => ({
          projectItems: state.projectItems.map((i) => i.id === itemId ? { ...i, [field]: value, ...(field === 'priceLabor' ? { priceIsManual: true } : {}) } : i),
        }));
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          scheduleProjectSync(`${projectId}:items`, () => {
            if (isStaleProjectSync(get, projectId)) return; // CR-01 guard
            const { projectItems, projectOverrides, safetyMargin } = get();
            void enqueueProjectChange(buildProjectItemsRecord(projectId, { projectItems, projectOverrides, safetyMargin }));
          });
        }
      },
      removeProjectItem: (itemId) => {
        set((state) => ({
          projectItems: state.projectItems.filter((i) => i.id !== itemId),
        }));
        // A deletion — including the LAST global item — must still enqueue: the
        // fresh read below can legitimately produce an empty projectItems array,
        // and buildProjectItemsRecord carries it as-is (no truthiness guard).
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          scheduleProjectSync(`${projectId}:items`, () => {
            if (isStaleProjectSync(get, projectId)) return; // CR-01 guard
            const { projectItems, projectOverrides, safetyMargin } = get();
            void enqueueProjectChange(buildProjectItemsRecord(projectId, { projectItems, projectOverrides, safetyMargin }));
          });
        }
      },
      addServicesToProject: (servicesToAdd) => {
        set((state) => {
          const newItems = createRoomItemsFromServices(servicesToAdd);
          return { projectItems: [...state.projectItems, ...newItems] };
        });
        const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
        if (projectId) {
          scheduleProjectSync(`${projectId}:items`, () => {
            if (isStaleProjectSync(get, projectId)) return; // CR-01 guard
            const { projectItems, projectOverrides, safetyMargin } = get();
            void enqueueProjectChange(buildProjectItemsRecord(projectId, { projectItems, projectOverrides, safetyMargin }));
          });
        }
      },

      resetRooms: () => {
        // Full reset: clear rooms (no auto-create dummy room).
        // After reset, user should land on the project selection/start page (StartPage).
        // Explicit room creation only happens from StartPage "create new" or EmptyState CTA.
        // WR-03 fix (36-REVIEW.md): projectItems/projectOverrides were previously left
        // untouched by a "full reset" — every other piece of per-project state was
        // cleared except these two, leaving leftover global-layer data attributable to
        // whichever project loads next (and feeding CR-01's cross-project leak via any
        // pending debounce timer that fires after this reset).
        set({
          rooms: [],
          projectItems: [],
          projectOverrides: {},
          safetyMargin: 0,
          meta: {},
          activeProjectId: null,
          actState: EMPTY_ACT_STATE,
        });
      },

      syncPrices: (servicesDb) => {
        let stats: SyncStats = { count: 0, diff: 0, nameChanged: 0, categoryChanged: 0, unitChanged: 0 };
        const roomsBefore = get().rooms;
        set((state) => {
          const result = syncProjectPrices(state.rooms, servicesDb);
          stats = result.stats;
          // Gate 1 (CATALOG-03 fix): apply the recomputed rooms unconditionally, not only
          // when the price-only counter is non-zero. syncProjectPrices returns the SAME
          // room/item references for anything it did not touch (pinned by
          // test/synchronizer.test.ts:89-101), so recomputing `rooms` costs nothing on a
          // genuine no-op and removes the entire class of "which axis did I forget"
          // regressions — a unitType-only or categoryId-only change used to be silently
          // discarded here because it never moved `count`.
          // WR-01 fix: Array.prototype.map (inside syncProjectPrices) always allocates a NEW
          // top-level array, even when every room reference is identical to its input — so an
          // unconditional `{ rooms: result.updatedRooms }` defeated startAutoSave's
          // `state.rooms === prevState.rooms` no-op guard (useProjectStore.ts below) on every
          // genuine no-op sync, scheduling a spurious background write. Reuse the previous
          // top-level reference (return `state`, i.e. no-op) when no room reference differs.
          const anyRoomChanged = result.updatedRooms.some((r, i) => r !== state.rooms[i])
            || result.updatedRooms.length !== state.rooms.length;
          return anyRoomChanged ? { rooms: result.updatedRooms } : state;
        });

        // Gate 2 (CATALOG-03 fix): enqueue on the full breakdown, not the price-only
        // counter alone — a name/category/unit-only change must reach the sync queue too.
        if (hasAnySyncChange(stats)) {
          const projectId = resolveProjectIdForEnqueue(get().activeProjectId);
          if (projectId) {
            // syncProjectPrices (src/entities/estimate/lib/synchronizer.ts) returns the SAME
            // room object reference for rooms whose items were unchanged, and a NEW room
            // object (with a new items array) only for rooms that actually changed — so
            // items-array identity is a reliable per-room changed/unchanged signal.
            const beforeById = new Map(roomsBefore.map((r) => [r.id, r]));
            for (const room of get().rooms) {
              const before = beforeById.get(room.id);
              if (before && before.items !== room.items) {
                // Reuse the debounce keyed by roomId so a bulk price sync across many
                // rooms still collapses with any in-flight per-room edit.
                triggerRoomSync(get, room.id);
              }
            }
          }
        }

        return stats;
      },

      createProjectSnapshot: (activeRoomId, name) => {
        const { rooms, projectItems, projectOverrides, safetyMargin, meta, activeProjectId, actState } = get();
        const snapshot = createSnapshot(rooms, activeRoomId, safetyMargin, name);
        snapshot.meta = meta;
        snapshot.projectItems = projectItems;
        snapshot.projectOverrides = projectOverrides;
        // DOC-03: persist actState on snapshot so acts join versioned project history (CLAUDE.md rule 11)
        snapshot.actState = actState;
        if (activeProjectId) snapshot.id = activeProjectId;
        return snapshot;
      },

      loadProjectSnapshot: (snapshot) => {
        if (!snapshot || !Array.isArray(snapshot.rooms)) return { activeRoomId: null };
        const newActiveId = snapshot.activeRoomId || (snapshot.rooms.length > 0 ? snapshot.rooms[0].id : null);
        // DOC-03: restore actState from snapshot; fall back to EMPTY_ACT_STATE for pre-Phase-7 snapshots (backward-compat)
        const parsedActState = snapshot.actState
          ? ActStateSchema.catch(EMPTY_ACT_STATE).parse(snapshot.actState)
          : EMPTY_ACT_STATE;
        set({
          rooms: snapshot.rooms,
          projectItems: snapshot.projectItems || [],
          projectOverrides: snapshot.projectOverrides || {},
          safetyMargin: snapshot.safetyMargin || 0,
          meta: snapshot.meta || {},
          activeProjectId: snapshot.id || null,
          actState: parsedActState,
        });
        return { activeRoomId: newActiveId };
      },

      appendAct: (act, updatedActState) => {
        // Validate the incoming ActState to guard against malformed data (CLAUDE.md rule 11/12)
        const validated = ActStateSchema.catch(EMPTY_ACT_STATE).parse(updatedActState);
        set({ actState: validated });
        // WR-03 fix: handle write-through rejection so failures surface rather than silently
        // losing the act on next page reload. Previously fire-and-forget (void).
        const { activeProjectId } = get();
        if (activeProjectId) {
          updateProjectEntry(activeProjectId, () => ({
            actState: validated,
          })).catch((err) => console.error('appendAct: failed to persist actState', err));
        }
        void act; // act is already embedded in updatedActState.acts; referenced for type clarity
      },

      commitVersion: async (versionName: string) => {
        const { rooms, projectItems, projectOverrides, meta, safetyMargin, activeProjectId, activeRoomId, actState } = get();
        if (!activeProjectId) return;
        const point = {
          rooms,
          projectItems,
          projectOverrides,
          meta,
          safetyMargin,
          // WR-03 fix: include actState in version snapshot so acts survive version restore
          actState,
          activeRoomId: activeRoomId || rooms[0]?.id || null,
          historyName: versionName,
          timestamp: new Date().toISOString(),
        };
        await updateProjectEntry(activeProjectId, (p) => ({
          history: ([point, ...(p.history ?? [])] as NonNullable<ProjectSnapshot['history']>).slice(0, 10),
        }));
      },

      initRooms: () => {
        // No longer auto-creates a default room.
        // Zero-room state is intentionally supported (see EmptyState onboarding guide).
        // Rooms are only created explicitly via user action (onAddRoom / handleCreateNew / addRoom).
      },

      startAutoSave: () => {
        let timer: ReturnType<typeof setTimeout> | null = null;

        const unsubscribe = useProjectStore.subscribe((state, prevState) => {
          if (!state.activeProjectId) return;
          if (
            state.rooms === prevState.rooms &&
            state.meta === prevState.meta &&
            state.safetyMargin === prevState.safetyMargin &&
            state.projectItems === prevState.projectItems &&
            state.projectOverrides === prevState.projectOverrides &&
            state.actState === prevState.actState
          ) return;

          if (timer) clearTimeout(timer);
          timer = setTimeout(async () => {
            const { rooms, projectItems, projectOverrides, meta, safetyMargin, activeProjectId, actState } = useProjectStore.getState();
            if (!activeProjectId) return;
            await updateProjectEntry(activeProjectId, () => ({
              rooms,
              projectItems,
              projectOverrides,
              meta,
              safetyMargin,
              actState,
            }));

            // FUNNEL-02: activation = first save with rooms >= 1 AND netTotal > 0.
            // Guard with localStorage flag (outside Zustand — must not trigger re-render).
            // Set the flag BEFORE calling track() to prevent double-fire on race (Pitfall 2).
            // Server SELECT-before-INSERT dedup (18-01) is the multi-device backstop.
            try {
              if (localStorage.getItem(ACTIVATION_LS_KEY) === null && rooms.length >= 1) {
                const totals = computeEstimateTotals(rooms, projectItems, safetyMargin, 0, projectOverrides);
                if (totals.netTotal > 0) {
                  localStorage.setItem(ACTIVATION_LS_KEY, '1');
                  track('activation', { room_count: rooms.length, total_gt_zero: true });
                }
              }
            } catch {
              // Non-fatal: localStorage failure (private mode / quota) must never break autosave (T-18-12)
            }
          }, 5000);
        });

        return () => {
          unsubscribe();
          if (timer) clearTimeout(timer);
        };
      },
    }),
    {
      name: 'estimatix-project',
      version: 2,
      storage: createJSONStorage(() => localStorage),
      // SAFE-05: defer auto-hydration until App.tsx's boot chain explicitly calls
      // persist.rehydrate() AFTER migrateIdbToZustand() has copied IndexedDB data into
      // localStorage — without this, the store hydrates synchronously at module-evaluation
      // time, strictly before that IDB copy can land, and the whole session runs on stale data.
      skipHydration: true,
      migrate: migrateProjectStore,
    },
  ),
);